import { apiRequest, VojomoLoader } from "@vojomo-core/shared";
import { useQBR } from "./use-qbr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./QBRTable.module.css";
import { EditorContent } from "@tiptap/react";
import { injectPersonalColumns } from "./qbr-table-html";
import { useQBREditor } from "./use-qbr-editor";
import { QBRCellCallbacks } from "./QBRTableCellExtension";
import "pages/TopicViewer/OutputStyle.css";

export const PersonalQBR = () => {
  const { getRowColor, qbrData, personalScoresList, setPersonalScoresList } =
    useQBR();
  const [editingRationaleRow, setEditingRationaleRow] = useState<number | null>(
    null
  );

  const scoresRef = useRef(personalScoresList);
  scoresRef.current = personalScoresList;

  const editingRef = useRef(editingRationaleRow);
  editingRef.current = editingRationaleRow;

  const personalCallbacks = useMemo<QBRCellCallbacks>(
    () => ({
      getScore: (rowIndex) => scoresRef.current?.[rowIndex]?.score ?? "",
      getRationale: (rowIndex) =>
        scoresRef.current?.[rowIndex]?.rationale ?? "",
      setScore: (rowIndex, value) => {
        const next = {
          ...scoresRef.current,
          [rowIndex]: {
            score: value,
            rationale: scoresRef.current?.[rowIndex]?.rationale || ""
          }
        };
        scoresRef.current = next;
        setPersonalScoresList(next);
      },
      setRationale: (rowIndex, value) => {
        const next = {
          ...scoresRef.current,
          [rowIndex]: {
            score: scoresRef.current?.[rowIndex]?.score || "",
            rationale: value
          }
        };
        scoresRef.current = next;
        setPersonalScoresList(next);
      },
      getRowColor,
      getEditingRow: () => editingRef.current,
      setEditingRow: (row) => setEditingRationaleRow(row)
    }),
    [getRowColor, setPersonalScoresList]
  );

  const tableHtml = useMemo(() => {
    if (!qbrData?.text) return null;
    return injectPersonalColumns(qbrData.text);
  }, [qbrData?.text]);

  const editor = useQBREditor({
    html: tableHtml,
    personalCallbacks
  });

  const saveScores = useCallback(async () => {
    await apiRequest<{
      success: boolean;
      score: number;
      active: boolean;
    }>({
      subRoute: "roadmap",
      path: `/update_objective_score`,
      method: "POST",
      body: {
        topicId: qbrData.topic_id,
        payload: {
          scores_list: personalScoresList
        }
      }
    });
  }, [qbrData?.topic_id, personalScoresList]);

  useEffect(() => {
    if (!personalScoresList) return;
    const timer = setTimeout(saveScores, 3000);
    return () => clearTimeout(timer);
  }, [personalScoresList]);

  // Re-render custom cells when scores or editing state change
  useEffect(() => {
    if (!editor) return;
    try {
      const tableEl = editor.view.dom.querySelector("table");
      if (!tableEl) return;
      tableEl
        .querySelectorAll("[data-qbr-score], [data-qbr-rationale]")
        .forEach((cell) => {
          const render = (cell as any).__qbrRender;
          if (typeof render === "function") render();
        });
    } catch {
      // Editor view not yet mounted
    }
  }, [editor, personalScoresList, editingRationaleRow]);

  if (!personalScoresList || !tableHtml) {
    return <VojomoLoader />;
  }

  return (
    <div className={`document-style ${styles.qbrWrapper}`}>
      <EditorContent editor={editor} className={styles.qbrEditorContent} />
    </div>
  );
};
